import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollReports = ({ payrollRecords, selectedPeriod, staffMembers }) => {
  const [reportType, setReportType] = useState('summary');
  const [selectedStaff, setSelectedStaff] = useState(null);

  const generatePayStub = (record) => {
    const staff = staffMembers?.find(s => s?.user_id === record?.staff_id);
    const payStubData = {
      employeeName: staff?.user_profiles?.full_name || 'Unknown',
      employeeEmail: staff?.user_profiles?.email || '',
      payPeriod: `${new Date(record?.pay_period_start)?.toLocaleDateString()} - ${new Date(record?.pay_period_end)?.toLocaleDateString()}`,
      regularHours: record?.regular_hours,
      overtimeHours: record?.overtime_hours,
      regularPay: parseFloat(record?.regular_pay || 0)?.toFixed(2),
      overtimePay: parseFloat(record?.overtime_pay || 0)?.toFixed(2),
      bonusAmount: parseFloat(record?.bonus_amount || 0)?.toFixed(2),
      grossPay: parseFloat(record?.gross_pay || 0)?.toFixed(2),
      totalDeductions: parseFloat(record?.total_deductions || 0)?.toFixed(2),
      netPay: parseFloat(record?.net_pay || 0)?.toFixed(2),
      deductions: record?.payroll_deductions || []
    };
    return payStubData;
  };

  const exportToCSV = () => {
    const headers = ['Employee', 'Pay Period Start', 'Pay Period End', 'Regular Hours', 'Overtime Hours', 'Gross Pay', 'Deductions', 'Net Pay', 'Status'];
    const rows = payrollRecords?.map(record => {
      const staff = staffMembers?.find(s => s?.user_id === record?.staff_id);
      return [
        staff?.user_profiles?.full_name || 'Unknown',
        record?.pay_period_start,
        record?.pay_period_end,
        record?.regular_hours,
        record?.overtime_hours,
        record?.gross_pay,
        record?.total_deductions,
        record?.net_pay,
        record?.payment_status
      ];
    });
    
    const csvContent = [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a?.setAttribute('href', url);
    a?.setAttribute('download', `payroll_report_${selectedPeriod?.start}_${selectedPeriod?.end}.csv`);
    a?.click();
  };

  const calculateSummary = () => {
    return {
      totalEmployees: new Set(payrollRecords?.map(r => r?.staff_id))?.size,
      totalGrossPay: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.gross_pay || 0), 0),
      totalDeductions: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.total_deductions || 0), 0),
      totalNetPay: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.net_pay || 0), 0),
      totalRegularHours: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.regular_hours || 0), 0),
      totalOvertimeHours: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.overtime_hours || 0), 0),
      totalBonuses: payrollRecords?.reduce((sum, r) => sum + parseFloat(r?.bonus_amount || 0), 0)
    };
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-xl font-semibold text-foreground">Payroll Reports & Export</h2>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline">
            <Icon name="Download" size={18} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setReportType('summary')}
          className={`px-4 py-2 font-medium transition-colors ${
            reportType === 'summary' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Summary Report
        </button>
        <button
          onClick={() => setReportType('detailed')}
          className={`px-4 py-2 font-medium transition-colors ${
            reportType === 'detailed' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Detailed Records
        </button>
        <button
          onClick={() => setReportType('paystubs')}
          className={`px-4 py-2 font-medium transition-colors ${
            reportType === 'paystubs' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Pay Stubs
        </button>
      </div>

      {/* Summary Report */}
      {reportType === 'summary' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Employees</p>
              <p className="text-2xl font-bold text-foreground">{summary?.totalEmployees}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Gross Pay</p>
              <p className="text-2xl font-bold text-green-600">${summary?.totalGrossPay?.toFixed(2)}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Deductions</p>
              <p className="text-2xl font-bold text-red-600">${summary?.totalDeductions?.toFixed(2)}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Net Pay</p>
              <p className="text-2xl font-bold text-primary">${summary?.totalNetPay?.toFixed(2)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Regular Hours</p>
              <p className="text-xl font-semibold text-foreground">{summary?.totalRegularHours?.toFixed(2)} hrs</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Overtime Hours</p>
              <p className="text-xl font-semibold text-foreground">{summary?.totalOvertimeHours?.toFixed(2)} hrs</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Bonuses</p>
              <p className="text-xl font-semibold text-foreground">${summary?.totalBonuses?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Records */}
      {reportType === 'detailed' && (
        <div className="overflow-x-auto">
          <table className="w-full border border-border rounded-lg">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Employee</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Period</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Hours</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Gross Pay</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Deductions</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Net Pay</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {payrollRecords?.map((record) => {
                const staff = staffMembers?.find(s => s?.user_id === record?.staff_id);
                const totalHours = parseFloat(record?.regular_hours || 0) + parseFloat(record?.overtime_hours || 0);
                
                return (
                  <tr key={record?.id} className="border-t border-border hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">
                      {staff?.user_profiles?.full_name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(record?.pay_period_start)?.toLocaleDateString()} - {new Date(record?.pay_period_end)?.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">
                      {totalHours?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">
                      ${parseFloat(record?.gross_pay)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right">
                      ${parseFloat(record?.total_deductions)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-foreground text-right">
                      ${parseFloat(record?.net_pay)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        record?.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        record?.payment_status === 'approved'? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {record?.payment_status?.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pay Stubs */}
      {reportType === 'paystubs' && (
        <div className="space-y-4">
          {payrollRecords?.map((record) => {
            const payStub = generatePayStub(record);
            
            return (
              <div key={record?.id} className="border border-border rounded-lg p-6 bg-card">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{payStub?.employeeName}</h3>
                    <p className="text-sm text-muted-foreground">{payStub?.employeeEmail}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Pay Period</p>
                    <p className="font-medium text-foreground">{payStub?.payPeriod}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Regular Hours</p>
                    <p className="font-semibold text-foreground">{payStub?.regularHours}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Overtime Hours</p>
                    <p className="font-semibold text-foreground">{payStub?.overtimeHours}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Regular Pay</p>
                    <p className="font-semibold text-foreground">${payStub?.regularPay}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Overtime Pay</p>
                    <p className="font-semibold text-foreground">${payStub?.overtimePay}</p>
                  </div>
                </div>

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Pay</span>
                    <span className="font-semibold text-foreground">${payStub?.grossPay}</span>
                  </div>
                  {payStub?.deductions?.map((deduction, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{deduction?.deduction_type}</span>
                      <span className="text-red-600">-${parseFloat(deduction?.amount)?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold pt-2 border-t border-border">
                    <span className="text-foreground">Total Deductions</span>
                    <span className="text-red-600">-${payStub?.totalDeductions}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-primary">
                    <span className="text-foreground">Net Pay</span>
                    <span className="text-primary">${payStub?.netPay}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {payrollRecords?.length === 0 && (
        <div className="text-center py-12 border border-border rounded-lg">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No payroll records for selected period</p>
        </div>
      )}
    </div>
  );
};

export default PayrollReports;